import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { Upload, Copy, X, Plus, Pencil, ArrowLeft } from 'lucide-react';
import {
  IMAGE_BUCKET,
  getStorageFileName,
  MONTHLY_STORAGE_FROM_YM,
  resolveMonthlyStoragePath,
  usesMonthlyStorageFolder,
} from './imageStoragePaths';

const formatYearMonthLabel = (ym) => {
  if (!ym || ym.length !== 6) return ym || '';
  return `${ym.slice(0, 4)}년 ${ym.slice(4, 6)}월`;
};

const toSortNoInputValue = (value) => {
  if (value == null || String(value).trim() === '') return '';
  return String(value);
};

/** DB text(YYYYMMDD 또는 YYYY-MM-DD) → date input 값 */
const ymdToDateInput = (ymd) => {
  if (!ymd) return '';
  const digits = String(ymd).replace(/\D/g, '');
  if (digits.length === 8) {
    return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(ymd).trim())) {
    return String(ymd).trim();
  }
  return '';
};

const dateInputToYmd = (value) => (value ? value.replace(/-/g, '') : '');

const BUCKET = IMAGE_BUCKET;
const MAX_SIZE_BYTES = 1000 * 1024; // 1MB
const ALLOWED_TYPE = 'image/png';
const MAX_DIMENSION = 1920; // 리사이즈 시 긴 변 최대 픽셀

/**
 * 이미지 리사이징 (가로세로 비율 유지)
 * @param {File} file
 * @param {number} maxSizeBytes
 * @returns {Promise<Blob>}
 */
const resizeImage = (file, maxSizeBytes) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;

      // 비율 유지하며 maxDimension 기준으로 축소
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        if (width >= height) {
          height = Math.round((height * MAX_DIMENSION) / width);
          width = MAX_DIMENSION;
        } else {
          width = Math.round((width * MAX_DIMENSION) / height);
          height = MAX_DIMENSION;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      const tryExport = (scale = 1) => {
        const w = Math.max(1, Math.round(width * scale));
        const h = Math.max(1, Math.round(height * scale));
        canvas.width = w;
        canvas.height = h;
        ctx.drawImage(img, 0, 0, w, h);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('이미지 변환 실패'));
              return;
            }
            if (blob.size <= maxSizeBytes || scale <= 0.25) {
              resolve(blob);
              return;
            }
            tryExport(scale * 0.8); // 80% 크기로 재시도
          },
          'image/png'
        );
      };

      tryExport(1);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('이미지 로드 실패'));
    };

    img.src = url;
  });
};

const ThumbnailPreview = ({ url, alt, onClick }) => {
  const [failed, setFailed] = useState(false);
  return (
    <div
      className="border rounded d-flex align-items-center justify-content-center bg-light"
      style={{ width: 64, height: 64, cursor: 'pointer', overflow: 'hidden' }}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
      aria-label="미리보기 확대"
    >
      {failed ? (
        <span className="small text-muted">없음</span>
      ) : (
        <img
          src={url}
          alt={alt}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
};

const emptyForm = {
  page_path: '',
  position_name: '',
  storage_path: '',
  description: '',
  sort_no: '',
  // version is managed automatically, not exposed in form
};

const ImageManagement = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const yearMonth = searchParams.get('ym') || '';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxItem, setLightboxItem] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState(null);
  const [sizeMap, setSizeMap] = useState({});
  const [refreshMap, setRefreshMap] = useState({}); // used to bust browser cache after new upload
  const [formModal, setFormModal] = useState(null); // null | { mode: 'new' } | { mode: 'edit', item }
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [sortEdits, setSortEdits] = useState({});
  const [savingSortId, setSavingSortId] = useState(null);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [savingPeriod, setSavingPeriod] = useState(false);

  useEffect(() => {
    if (!yearMonth || yearMonth.length !== 6) {
      navigate('/image-management', { replace: true });
      return;
    }
    fetchItems();
  }, [yearMonth, navigate]);

  useEffect(() => {
    if (items.length > 0) {
      fetchFileSizes(items.map((i) => i.storage_path));
    }
  }, [items]);

  const fetchFileSizes = async (storagePaths) => {
    const folderPaths = new Set();
    storagePaths.forEach((p) => {
      const idx = p.lastIndexOf('/');
      folderPaths.add(idx >= 0 ? p.slice(0, idx) : '');
    });

    const map = {};
    for (const folder of folderPaths) {
      try {
        const { data, error } = await supabase.storage.from(BUCKET).list(folder || '', { limit: 500 });
        if (error) continue;
        (data || []).forEach((f) => {
          const fullPath = folder ? `${folder}/${f.name}` : f.name;
          const size = f.metadata?.size ?? f.size;
          if (size != null) map[fullPath] = Number(size);
        });
      } catch {
        /* ignore */
      }
    }
    setSizeMap((prev) => ({ ...prev, ...map }));
  };

  const formatFileSize = (bytes) => {
    if (bytes == null) return '-';
    if (bytes < 1024) return `${bytes}B`;
    return `${(bytes / 1024).toFixed(1)}KB`;
  };

  const fetchItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('page_image_management')
        .select('*')
        .eq('year_month', yearMonth)
        .order('sort_no', { ascending: true, nullsFirst: false });
      if (error) throw error;
      const rows = data || [];
      setItems(rows);
      const edits = {};
      rows.forEach((row) => {
        edits[row.id] = toSortNoInputValue(row.sort_no);
      });
      setSortEdits(edits);

      const periodRow = rows.find((row) => row.start_ymd || row.end_ymd) || rows[0];
      if (periodRow) {
        setPeriodStart(ymdToDateInput(periodRow.start_ymd));
        setPeriodEnd(ymdToDateInput(periodRow.end_ymd));
      } else {
        setPeriodStart('');
        setPeriodEnd('');
      }
    } catch (e) {
      setError(e.message || '데이터 조회 실패');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const getPublicUrl = (storagePath) => {
    const { data } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);
    let url = data.publicUrl;
    // append cache-busting query when we've recently re-uploaded the same path
    const stamp = refreshMap[storagePath];
    if (stamp) {
      url += `?cache=${stamp}`;
    }
    return url;
  };

  const handleCopyUrl = async (storagePath) => {
    const url = getPublicUrl(storagePath);
    try {
      await navigator.clipboard.writeText(url);
      setCopyFeedback(storagePath);
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch (e) {
      alert('URL 복사에 실패했습니다.');
    }
  };

  const validateFile = (file) => {
    if (file.type !== ALLOWED_TYPE) {
      return 'PNG 형식의 이미지만 업로드 가능합니다.';
    }
    return null;
  };

  const getFileToUpload = async (file) => {
    // 500KB 이하이면 리사이징 없이 그대로 사용
    if (file.size <= MAX_SIZE_BYTES) {
      return file;
    }
    // 500KB 초과 시 리사이징 (가로세로 비율 유지)
    return resizeImage(file, MAX_SIZE_BYTES);
  };

  const openAddModal = () => {
    setFormData(emptyForm);
    setFormModal({ mode: 'new' });
  };

  const openEditModal = (item) => {
    setFormData({
      page_path: item.page_path || '',
      position_name: item.position_name || '',
      storage_path: item.storage_path || '',
      description: item.description || '',
      sort_no: toSortNoInputValue(item.sort_no),
    });
    setFormModal({ mode: 'edit', item });
  };

  const closeFormModal = () => {
    setFormModal(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.page_path?.trim() || !formData.position_name?.trim() || !formData.storage_path?.trim()) {
      alert('페이지 경로, 위치명, 저장 경로는 필수입니다.');
      return;
    }
    const listYearMonth =
      formModal.mode === 'edit' ? formModal.item.year_month || yearMonth : yearMonth;
    setSaving(true);
    try {
      const storage_path = resolveMonthlyStoragePath(formData.storage_path, listYearMonth);
      const file_name = getStorageFileName(storage_path);
      const payload = {
        page_path: formData.page_path.trim(),
        position_name: formData.position_name.trim(),
        storage_path,
        file_name: file_name || null,
        description: formData.description?.trim() || null,
        year_month: listYearMonth,
        sort_no: formData.sort_no?.trim() || null,
      };
      if (formModal.mode === 'new') {
        payload.is_active = 'N';
        payload.version = 1;
        if (periodStart) payload.start_ymd = dateInputToYmd(periodStart);
        if (periodEnd) payload.end_ymd = dateInputToYmd(periodEnd);
        const { error } = await supabase.from('page_image_management').insert(payload);
        if (error) throw error;
        alert('등록되었습니다.');
      } else {
        const { error } = await supabase
          .from('page_image_management')
          .update(payload)
          .eq('id', formModal.item.id);
        if (error) throw error;
        alert('수정되었습니다.');
      }
      closeFormModal();
      fetchItems();
    } catch (err) {
      alert(err.message || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleSortEditChange = (id, value) => {
    setSortEdits((prev) => ({ ...prev, [id]: value }));
  };

  const savePeriod = async () => {
    if (!periodStart || !periodEnd) {
      alert('시작일과 종료일을 입력해주세요.');
      return;
    }
    const start_ymd = dateInputToYmd(periodStart);
    const end_ymd = dateInputToYmd(periodEnd);
    if (start_ymd.length !== 8 || end_ymd.length !== 8) {
      alert('날짜 형식이 올바르지 않습니다.');
      return;
    }
    if (start_ymd > end_ymd) {
      alert('시작일은 종료일보다 이후일 수 없습니다.');
      return;
    }

    setSavingPeriod(true);
    try {
      const { error } = await supabase
        .from('page_image_management')
        .update({ start_ymd, end_ymd })
        .eq('year_month', yearMonth);
      if (error) throw error;
      alert('기간이 저장되었습니다.');
      await fetchItems();
    } catch (e) {
      alert(e.message || '기간 저장에 실패했습니다.');
    } finally {
      setSavingPeriod(false);
    }
  };

  const saveSortNo = async (item) => {
    const nextValue = sortEdits[item.id]?.trim() || null;
    const currentValue = item.sort_no?.trim() || null;
    if (nextValue === currentValue) return;

    setSavingSortId(item.id);
    try {
      const { error } = await supabase
        .from('page_image_management')
        .update({ sort_no: nextValue })
        .eq('id', item.id);
      if (error) throw error;
      await fetchItems();
    } catch (e) {
      alert(e.message || '정렬 순서 저장에 실패했습니다.');
      setSortEdits((prev) => ({ ...prev, [item.id]: toSortNoInputValue(item.sort_no) }));
    } finally {
      setSavingSortId(null);
    }
  };

  const handleUpload = async (item) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const err = validateFile(file);
      if (err) {
        alert(err);
        return;
      }

      setUploadingId(item.id);
      try {
        const fileOrBlob = await getFileToUpload(file);
        const blob = fileOrBlob instanceof Blob ? fileOrBlob : file;
        const finalFile = blob instanceof File ? blob : new File([blob], file.name, { type: 'image/png' });

        const listYearMonth = item.year_month || yearMonth;
        const uploadPath = resolveMonthlyStoragePath(item.storage_path, listYearMonth);

        const { error } = await supabase.storage
          .from(BUCKET)
          .upload(uploadPath, finalFile, { upsert: true, contentType: 'image/png' });

        if (error) throw error;
        alert('이미지가 업로드되었습니다.');
        try {
          const newVersion = (item.version ?? 0) + 0.1;
          const dbUpdate = {
            version: newVersion,
            file_name: getStorageFileName(uploadPath) || null,
          };
          if (uploadPath !== item.storage_path) {
            dbUpdate.storage_path = uploadPath;
          }
          await supabase.from('page_image_management').update(dbUpdate).eq('id', item.id);
        } catch (verErr) {
          console.warn('version bump failed', verErr);
        }
        setRefreshMap((prev) => ({ ...prev, [uploadPath]: Date.now() }));
        fetchItems();
      } catch (e) {
        console.error(e);
        alert(e.message || '업로드에 실패했습니다.');
      } finally {
        setUploadingId(null);
      }
    };
    input.click();
  };

  if (!yearMonth || yearMonth.length !== 6) {
    return null;
  }

  if (loading) {
    return (
      <div className="card shadow mb-4">
        <div className="card-body">로딩 중...</div>
      </div>
    );
  }

  return (
    <div className="card shadow mb-4">
      <div className="card-header py-3 d-flex justify-content-between align-items-center">
        <h6 className="m-0 font-weight-bold text-primary">
          이미지 관리 — {formatYearMonthLabel(yearMonth)}
        </h6>
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <button type="button" className="btn btn-success btn-sm" onClick={openAddModal}>
            <Plus size={16} className="mr-1" />
            추가
          </button>
          <button
            type="button"
            className="btn btn-sm"
            style={{ backgroundColor: '#f97316', borderColor: '#f97316', color: '#fff' }}
            onClick={() => navigate('/image-management')}
          >
            <ArrowLeft size={16} className="mr-1" />
            목록으로
          </button>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="alert alert-danger">{error}</div>}
        <div className="card bg-light mb-3">
          <div className="card-body py-3">
            <div className="d-flex flex-wrap align-items-end" style={{ gap: 12 }}>
              <div className="form-group mb-0">
                <label className="small font-weight-bold mb-1">시작일</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                  disabled={savingPeriod}
                />
              </div>
              <div className="form-group mb-0">
                <label className="small font-weight-bold mb-1">종료일</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  disabled={savingPeriod}
                />
              </div>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={savePeriod}
                disabled={savingPeriod || items.length === 0}
              >
                {savingPeriod ? '저장 중...' : '기간 저장'}
              </button>
            </div>
            <small className="text-muted d-block mt-2">
              {formatYearMonthLabel(yearMonth)}의 모든 이미지 항목에 동일하게 적용됩니다.
            </small>
          </div>
        </div>
        <div className="mb-3">
          <small className="text-muted">PNG 형식. 500KB 초과 시 가로세로 비율 유지하며 자동 리사이징됩니다.</small>
        </div>
        <div className="table-responsive">
          <table className="table table-bordered">
            <thead className="thead-light">
              <tr>
                <th style={{ width: 90 }}>정렬 순서</th>
                <th>페이지 경로</th>
                <th>위치명</th>
                <th>버전</th>
                <th style={{ width: 100 }}>미리보기</th>
                <th>이미지 크기</th>
                <th>URL</th>
                <th style={{ width: 180 }}>액션</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center text-muted">
                    등록된 이미지가 없습니다. 추가 버튼으로 등록하세요.
                  </td>
                </tr>
              ) : (
                items.map((item) => {
                  const url = getPublicUrl(item.storage_path);
                  const isUploading = uploadingId === item.id;
                  return (
                    <tr key={item.id}>
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={toSortNoInputValue(sortEdits[item.id])}
                          onChange={(e) => handleSortEditChange(item.id, e.target.value)}
                          onBlur={() => saveSortNo(item)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              e.target.blur();
                            }
                          }}
                          placeholder=""
                          disabled={savingSortId === item.id}
                          style={{ width: 72 }}
                        />
                      </td>
                      <td><code>{item.page_path}</code></td>
                      <td>{item.position_name}</td>
                      <td>{item.version ?? '-'}</td>
                      <td>
                        <ThumbnailPreview url={url} alt={item.position_name} onClick={() => setLightboxItem(item)} />
                      </td>
                      <td>{formatFileSize(sizeMap[item.storage_path])}</td>
                      <td>
                        <small className="text-break d-block" style={{ maxWidth: 280 }}>{url}</small>
                      </td>
                      <td>
                        <div className="d-flex align-items-center" style={{ gap: 4 }}>
                          {copyFeedback === item.storage_path && (
                            <span className="badge badge-success mr-1">복사됨</span>
                          )}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => handleCopyUrl(item.storage_path)}
                            title="URL 복사"
                          >
                            <Copy size={14} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => openEditModal(item)}
                            title="수정"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => handleUpload(item)}
                            disabled={isUploading}
                            title="업로드/교체"
                          >
                            <Upload size={14} className="mr-1" />
                            {isUploading ? '중...' : '업로드'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 추가/수정 모달 */}
      {formModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{formModal.mode === 'new' ? '이미지 항목 추가' : '이미지 항목 수정'}</h5>
                <button type="button" className="close" onClick={closeFormModal} aria-label="닫기">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <form onSubmit={handleFormSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>페이지 경로</label>
                    <input
                      type="text"
                      className="form-control"
                      name="page_path"
                      value={formData.page_path}
                      onChange={handleFormChange}
                      placeholder="app/today_qt/page.tsx"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>위치명</label>
                    <input
                      type="text"
                      className="form-control"
                      name="position_name"
                      value={formData.position_name}
                      onChange={handleFormChange}
                      placeholder="QT Card Background"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>저장 경로 (파일명)</label>
                    <input
                      type="text"
                      className="form-control"
                      name="storage_path"
                      value={formData.storage_path}
                      onChange={handleFormChange}
                      placeholder="missionqt_header.png"
                      required
                    />
                    <small className="form-text text-muted">
                      목록 년월(<code>{yearMonth}</code>) 기준으로 파일명 뒤에{' '}
                      <code>_{yearMonth}</code>가 자동으로 붙습니다. 입력 경로에 다른 년월이
                      있어도 무시됩니다.
                      {usesMonthlyStorageFolder(yearMonth) ? (
                        <>
                          {' '}
                          ({MONTHLY_STORAGE_FROM_YM}월부터 <code>{yearMonth}/</code> 폴더 — 예:{' '}
                          <code>
                            {yearMonth}/missionqt_header_{yearMonth}.png
                          </code>
                          )
                        </>
                      ) : (
                        <>
                          {' '}
                          (예: <code>missionqt_header_{yearMonth}.png</code>)
                        </>
                      )}
                    </small>
                  </div>
                  <div className="form-group">
                    <label>설명</label>
                    <textarea
                      className="form-control"
                      name="description"
                      value={formData.description}
                      onChange={handleFormChange}
                      rows="2"
                    />
                  </div>
                  <div className="form-group mb-0">
                    <label>정렬 순서 (sort_no)</label>
                    <input
                      type="text"
                      className="form-control"
                      name="sort_no"
                      value={formData.sort_no}
                      onChange={handleFormChange}
                      placeholder="1"
                    />
                    <small className="form-text text-muted">목록에서도 바로 수정할 수 있습니다.</small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeFormModal}>
                    취소
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? '저장 중...' : '저장'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxItem && (
        <div
          className="modal show d-block"
          tabIndex={-1}
          style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
          onClick={() => setLightboxItem(null)}
          role="dialog"
          aria-modal="true"
          aria-label="이미지 미리보기"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header py-2 d-flex justify-content-between align-items-center">
                <h6 className="modal-title mb-0">{lightboxItem.position_name}</h6>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setLightboxItem(null)}
                  aria-label="닫기"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="modal-body p-3 text-center">
                <img
                  src={getPublicUrl(lightboxItem.storage_path)}
                  alt={lightboxItem.position_name}
                  style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                  onError={(e) => {
                    e.target.alt = '이미지를 불러올 수 없습니다.';
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="150"%3E%3Crect fill="%23eee" width="200" height="150"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageManagement;
